import axios from "axios";
import API_INTERFACE_URI from "../constants/uri.constants";
import baseService from "./service";
import {globalHeaders} from "../utilities/http";
import {mapPrefecture} from "../utilities/prefectureMapper";

/**
 * Map API response to UI format
 * @param {Object} apiResponse - Raw API response
 * @returns {Object} Mapped response for UI consumption
 */
function mapFailedAddressesResponse(apiResponse) {
    const records = (apiResponse.failedFFRs || []).map(item => ({
        ffrNumber: item.ffrNumber,
        orderReference: item.consumerSalesOrderNumber || '',
        coNumber: item.consumerSalesOrderNumber,
        lastUpdated: item.lastUpdateDateTime,
        updatedBy: item.lastUpdatedUser,
        original: {
            postalCode: item.originalAddress?.postalCode ?? "",
            state: mapPrefecture(item.originalAddress?.stateProvinceCode),
            city: item.originalAddress?.cityName ?? "",
            addressLine1: item.originalAddress?.addressLine1Text ?? "",
            addressLine2: item.originalAddress?.addressLine2Text ?? "",
            addressLine3: item.originalAddress?.addressLine3Text ?? ""
        },
        proposed: {
            postalCode: item.proposedAddress?.postalCode ?? "",
            state: mapPrefecture(item.proposedAddress?.stateProvinceCode),
            city: item.proposedAddress?.cityName ?? "",
            addressLine1: item.proposedAddress?.addressLine1Text ?? "",
            addressLine2: item.proposedAddress?.addressLine2Text ?? "",
            addressLine3: item.proposedAddress?.addressLine3Text ?? ""
        }
    }));

    return {
        records: records,
        nextPageToken: apiResponse.nextPageToken || null,
        lastPage: apiResponse.lastPage ?? true,
        totalCount: apiResponse.totalCount != null ? apiResponse.totalCount : null
    };
}

/**
 * Get list of failed addresses with pagination and search
 * @param {string} destination_location_code - DC value
 * @param {string} search - Search term for filtering FFR numbers
 * @param {string} pageToken - Token for pagination (null for first page)
 * @param {number} pageSize - Items per page
 * @returns {Promise} Promise with address exception data
 */
function getFailedAddresses(destination_location_code, search = "", pageToken = null, pageSize = 10) {
    let params = [];
    let filters = [];
    
     if (destination_location_code) {
        filters.push(`destination_location_code(${destination_location_code})`);
     }
     if (search) {
        filters.push(`ffrNumber(${search})`);
    }
    if (filters.length > 0) {
        params.push(`filters=${filters.join(",")}`);
    }
    params.push(`pageSize=${pageSize}`);
    if (pageToken) {
        params.push(`nextPageToken=${encodeURIComponent(pageToken)}`);
    }
    
    let url = API_INTERFACE_URI.GET_FAILED_ADDRESSES;
    url = url + "?" + params.join("&");
    return baseService.get(url, globalHeaders).then(response => {
        return {
            data: mapFailedAddressesResponse(response.data)
        };
    });
}

/**
 * Update retry FFR address with proposed changes
 * @param {Object} payload - Address update payload
 * @param {string} payload.ffrNumber - FFR number to retry
 * @param {string} payload.destination_location_code - DC value
 * @param {Object} payload.proposedAddress - Proposed address details
 * @returns {Promise} Promise with update result
 */
function updateRetryAddress(payload) {
    let url = API_INTERFACE_URI.UPDATE_RETRY_FFR_ADDRESS;
    return baseService.post(url, globalHeaders, payload);
}

const DC_NAME_MAP = {
    "1081": "FLIGHT",
    "1093": "IMPACT"
};

/**
 * Download all CD_Failed addresses as CSV.
 *
 * If the user has an active bulk upload in progress the backend returns 409.
 * Callers should catch that status and show the user a warning popup.
 * Passing force=true skips the check (used when user clicks "Download Anyway").
 *
 * @param {string} destination_location_code - DC value
 * @param {string} username                  - Logged-in username for the active-upload guard
 * @param {boolean} [force=false]            - Set true to bypass the active-upload guard
 * @returns {Promise} Resolves on successful download; rejects with the Axios error on 409/5xx
 */
/**
 * Case 1 — normal download  : force=false  → API checks for active upload, returns 409 if found
 * Case 2 — "Download Anyway": force=true   → API skips the active-upload guard
 */
function downloadFailedAddressesCsv(destination_location_code, username, force = false) {
    const url = `${API_INTERFACE_URI.DOWNLOAD_FAILED_ADDRESSES}`
        + `?filters=destination_location_code(${encodeURIComponent(destination_location_code)})`
        + (username ? `&username=${encodeURIComponent(username)}` : '')
        + `&force=${force}`;

    // Use axios directly — responseType:'blob' is not forwarded by baseService.get()
    return axios.get(url, {
        headers: globalHeaders,
        responseType: 'blob',
        // Allow 409 through so we can inspect the JSON body
        validateStatus: status => status === 200 || status === 409
    }).then(response => {
        if (response.status === 409) {
            // Active upload in progress — parse JSON from blob and rethrow as structured error
            return response.data.text().then(text => {
                const payload = JSON.parse(text);
                const err = new Error('ACTIVE_UPLOAD');
                err.activeUploadInfo = payload;
                throw err;
            });
        }
        // Backend already prepends UTF-8 BOM — create Blob from raw bytes as-is.
        const blob = new Blob([response.data], { type: 'text/csv;charset=utf-8;' });
        const downloadUrl = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = downloadUrl;
        const dcName = DC_NAME_MAP[destination_location_code] || destination_location_code;
        const now = new Date();
        const ts = String(now.getDate()).padStart(2, '0')
            + String(now.getMonth() + 1).padStart(2, '0')
            + now.getFullYear() + '_'
            + String(now.getHours()).padStart(2, '0')
            + String(now.getMinutes()).padStart(2, '0')
            + String(now.getSeconds()).padStart(2, '0');
        link.setAttribute('download', `FAILED_FFR_${dcName}_${ts}.csv`);
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(downloadUrl);
    });
}

/**
 * Upload a corrected CSV file for bulk retry processing.
 * @param {File}   file       - The CSV File object from the file input
 * @param {string} dcCode     - Destination location code (e.g. "1081")
 * @param {string} uploadedBy - Username of the uploader
 * @returns {Promise}
 */
function bulkRetryUpload(file, dcCode, uploadedBy) {
    const formData = new FormData();
    formData.append("file", file);
    formData.append("dcCode", dcCode);
    if (uploadedBy) {
        formData.append("uploadedBy", uploadedBy);
    }

    const headers = { ...globalHeaders };
    delete headers["Content-Type"];

    return axios.post(API_INTERFACE_URI.BULK_RETRY_UPLOAD, formData, { headers });
}

/**
 * Fetch bulk upload history for a DC (newest first, last 50 batches).
 * @param {string} dcCode - Destination location code
 * @returns {Promise<{ history: Array, totalCount: number }>}
 */
function getUploadHistory(dcCode) {
    const url = `${API_INTERFACE_URI.BULK_UPLOAD_HISTORY}?dcCode=${encodeURIComponent(dcCode)}`;
    return baseService.get(url, globalHeaders).then(r => r.data);
}

/**
 * Refresh the latest status for a single upload batch.
 * @param {string} fileId - The batch file identifier ("userId#dcId#epochMs")
 * @returns {Promise<UploadHistoryItem>}
 */
function refreshUploadBatch(fileId) {
    const url = `${API_INTERFACE_URI.BULK_UPLOAD_HISTORY_BATCH}?fileId=${encodeURIComponent(fileId)}`;
    return baseService.get(url, globalHeaders).then(r => r.data);
}

/**
 * Download the failed rows CSV for a given upload batch.
 * @param {string} fileId  - The batch file identifier ("userId#dcId#epochMs")
 * @param {string} dcCode  - DC code (e.g. "1081")
 */
function downloadFailedRowsCsv(fileId, dcCode) {
    const url = `${API_INTERFACE_URI.BULK_UPLOAD_HISTORY_DOWNLOAD}?fileId=${encodeURIComponent(fileId)}`;
    // Use axios directly — baseService.get() treats its 2nd arg as headers,
    // so responseType:'blob' would never reach Axios as a config option there.
    return axios.get(url, {
        headers: globalHeaders,
        responseType: 'blob'
    }).then(response => {
        // Backend already prepends UTF-8 BOM — create Blob from raw bytes as-is.
        const blob = new Blob([response.data], { type: 'text/csv;charset=utf-8;' });
        const downloadUrl = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = downloadUrl;
        const dcName = DC_NAME_MAP[dcCode] || dcCode;
        const now = new Date();
        const ts = String(now.getDate()).padStart(2, '0')
            + String(now.getMonth() + 1).padStart(2, '0')
            + now.getFullYear() + '_'
            + String(now.getHours()).padStart(2, '0')
            + String(now.getMinutes()).padStart(2, '0')
            + String(now.getSeconds()).padStart(2, '0');
        link.setAttribute('download', `FAILED_FFR_${dcName}_${ts}.csv`);
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(downloadUrl);
    });
}

const AddressExceptionService = {
    getFailedAddresses,
    updateRetryAddress,
    downloadFailedAddressesCsv,
    bulkRetryUpload,
    getUploadHistory,
    refreshUploadBatch,
    downloadFailedRowsCsv,
};

export default AddressExceptionService;
